import { useContext, useId, useRef, useState } from 'react';

import {
  DraggableProvided,
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import { Collapse, Stack, StackProps, styled, Typography } from '@mui/material';

import { tokens } from '../../../../../../../theme/generated/tokens';
import { ExpandIcon, LockIcon } from './icons';
import { mergeSx } from '../../../../../../../utils';
import { Checkbox } from '../../../../../../controls';
import { Tooltip } from '../../../../../../tooltip';
import { DragIcon } from '../../../../../components/icons';
import { useRowBaseModel } from '../_hooks/useRowBaseModel';
import { useRowLockingWithAnimation } from '../_hooks/useRowLockingWithAnimation';
import { SettingsDrawerContext } from '../context';
import {
  isColumnsGroup,
  SettingsColumn,
  SettingsGroupedColumn,
  SettingsRowModel,
} from '../types';

const StyledDragIcon = styled(DragIcon, {
  shouldForwardProp: (prop) => prop !== 'reorderSettingDisabled',
})<{
  reorderSettingDisabled?: boolean;
}>(({ reorderSettingDisabled }) => ({
  backgroundColor: 'inherit',
  display: 'flex',
  color: reorderSettingDisabled
    ? tokens.theme.disabled.fg
    : tokens.theme.fg.structural.secondary,
  '&:hover': {
    color: !reorderSettingDisabled
      ? tokens.theme.fg.structural.primary
      : undefined,
  },
}));

const StyledExpandIcon = styled(ExpandIcon)({
  cursor: 'pointer',
});

type DndProps = {
  dragHandleProps?: DraggableProvidedDragHandleProps;
  dragContainerProps?: DraggableProvidedDraggableProps;
  dragRef?: DraggableProvided['innerRef'];
};

type RowBaseProps = Omit<StackProps, 'title'> & {
  column: SettingsColumn;
  childColumn?: boolean;
  groupHeaderColumn?: boolean;
  switchLocking?: (locked: boolean) => void;
} & DndProps;
const RowBase = ({
  children,
  dragHandleProps,
  dragContainerProps,
  dragRef,
  column,
  childColumn,
  switchLocking: parentSwitchLocking,
  ...stackProps
}: RowBaseProps) => {
  const titleId = useId();
  const { switchColumnVisibility } = useContext(SettingsDrawerContext);
  const ref = useRef<HTMLElement | null>(null);
  const isGroup = isColumnsGroup(column);
  const { switchLocking, lockingSx } = useRowLockingWithAnimation({
    id: column.id,
    row: ref.current,
  });

  const {
    title,
    frozen,
    visibilitySettingTooltip,
    lockSettingDisabled,
    lockSettingTooltip,
    reorderSettingDisabled,
    reorderSettingTooltip,
    group,
  } = useRowBaseModel(column);

  return (
    <Stack
      direction="row"
      py={8}
      px={32}
      pl={childColumn ? 64 : undefined}
      alignItems="center"
      {...stackProps}
      sx={mergeSx(
        {
          borderBottom: `1px solid ${tokens.theme.border.structural.neutralSubtle}`,
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.surfaceHover,
          },
        },
        stackProps.sx,
        lockingSx
      )}
      {...(dragContainerProps || {})}
      ref={(r) => {
        dragRef?.(r);
        ref.current = r;
      }}
    >
      <Tooltip
        title={visibilitySettingTooltip}
        disableHoverListener={!visibilitySettingTooltip}
      >
        <div>
          <Checkbox
            aria-labelledby={titleId}
            testId={`column-settings-${column.id}`}
            checked={typeof column.visible === 'string' ? true : column.visible}
            indeterminate={column.visible === 'indeterminate'}
            onChange={(_, checked) =>
              switchColumnVisibility?.({
                id: column.id,
                visible: checked,
                group,
              })
            }
            size="small"
            disabled={frozen}
            sx={{ mr: 16 }}
          />
        </div>
      </Tooltip>
      <Typography
        id={titleId}
        component="div"
        variant="L10B"
        sx={
          isGroup
            ? {
                border: `1px solid ${tokens.theme.border.structural.neutral}`,
                borderRadius: tokens.theme.radius.sm,
                px: 4,
                py: 2,
                mr: 8,
              }
            : undefined
        }
      >
        {title}
      </Typography>
      {children}
      {!childColumn && (
        <Stack direction="row" gap={8} ml="auto" alignItems="center">
          <Tooltip
            title={lockSettingTooltip}
            disableHoverListener={!lockSettingTooltip}
          >
            <LockIcon
              isDisabled={lockSettingDisabled}
              isLocked={column.locked}
              onClick={
                lockSettingDisabled
                  ? undefined
                  : () => (parentSwitchLocking || switchLocking)(!column.locked)
              }
            />
          </Tooltip>
          <Tooltip
            title={reorderSettingTooltip}
            disableHoverListener={!reorderSettingTooltip}
          >
            <StyledDragIcon
              {...dragHandleProps}
              reorderSettingDisabled={reorderSettingDisabled}
            />
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};

type SettingGroupRowProps = { group: SettingsGroupedColumn } & DndProps;
const SettingsGroupRow = ({
  group,
  dragHandleProps,
  dragRef,
  dragContainerProps,
}: SettingGroupRowProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { switchLocking, lockingSx } = useRowLockingWithAnimation({
    id: group.id,
    row: ref.current,
  });

  return (
    <Stack
      ref={(r) => {
        dragRef?.(r);
        ref.current = r;
      }}
      sx={mergeSx(
        {
          borderBottom: `1px solid ${tokens.theme.border.structural.neutralSubtle}`,
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.surfaceHover,
          },
        },
        lockingSx
      )}
      {...(dragContainerProps || {})}
    >
      <RowBase
        column={group}
        dragHandleProps={dragHandleProps}
        switchLocking={switchLocking}
        sx={{ borderBottom: 'none' }}
      >
        <StyledExpandIcon
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </RowBase>
      <Collapse in={isExpanded}>
        <Stack>
          {group.columns.map((col) => (
            <RowBase
              key={col.id}
              column={col}
              sx={{ borderBottom: 'none' }}
              childColumn
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

type SettingsRowProps = { column: SettingsRowModel } & DndProps;
export const SettingsRow = ({ column, ...rest }: SettingsRowProps) => {
  if (isColumnsGroup(column)) {
    return <SettingsGroupRow group={column} {...rest} />;
  } else {
    return <RowBase column={column} {...rest} />;
  }
};
